/**
 * Maps column keys to their unit types
 * 
 */
const COLUMN_UNIT_MAP = {
  feed_temp: "temperature",
  feed_flowrate: "flow",
  concentrate_flowrate: "flow",
  feed_pressure: "pressure",
  feed_quality: "feed_quality",
  permeate_flowrate: "flow",
  permeate_pressure: "pressure",
  permeate_quality: "permeate_quality",
  concentrate_pressure: "pressure",
  pressure_drop: "pressure",
  norm_permeate_flow: "flow",
  norm_pressure_drop: "pressure"
};

/**
 * Gets column header using hardcoded translations and includes units
 * @param {Function} t - Translation function
 * @param {string} key - Column key
 * @param {Object} units - Units object with properties like temperature, pressure, flow, etc.
 * @returns {string} Translated column name with units
 */
const getColumnTranslation = (t, key, units = {}) => {
  const translations = {
    date: "ReplacementPredictionStrings.date",
    feed_temp: "referenceConditions.feed_temp",
    feed_flowrate: "referenceConditions.feed_flowrate",
    concentrate_flowrate: "referenceConditions.concentrate_flowrate",
    feed_pressure: "referenceConditions.feed_pressure",
    feed_quality: "referenceConditions.feed_quality",
    permeate_flowrate: "referenceConditions.permeate_flowrate",
    permeate_pressure: "referenceConditions.permeate_pressure",
    permeate_quality: "referenceConditions.permeate_quality",
    concentrate_pressure: "referenceConditions.concentrate_pressure",
    pressure_drop: "referenceConditions.pressure_drop",
    norm_permeate_flow: "ReplacementPredictionStrings.norm_permeate_flow",
    norm_pressure_drop: "ReplacementPredictionStrings.norm_pressure_drop",
    norm_salt_passage: "ReplacementPredictionStrings.norm_salt_passage",
  };
  
  let columnHeader = t(translations[key] || key);
  
  // Add unit to the column header if available
  const unitType = COLUMN_UNIT_MAP[key];
  if (unitType && units[unitType]) {
    columnHeader = `${columnHeader} (${units[unitType]})`;
  }
  
  return columnHeader;
};

/**
 * Creates a Processed Data Excel sheet with the cleaned operational data
 * @param {Object} workbook - ExcelJS workbook instance
 * @param {Array|null} sheetData - Array of row objects with processed data
 * @param {string} sheetName - Name for the worksheet
 * @param {Function} t - Translation function from i18next
 * @param {Object} units - Units object with temperature, pressure, flow, feed_quality, permeate_quality
 */
export const createProcessedDataExcelSheet = (workbook, sheetData, sheetName, t, units = {}) => {
    
    // Handle empty or invalid data
    if (!Array.isArray(sheetData) || sheetData.length === 0) {
        return;
    }
    
    const columnKeys = Object.keys(sheetData[0] || {});

    if (columnKeys.length === 0) {
      return;
    }

    const excelSheet = workbook.addWorksheet(sheetName);
    try {
    // Get column keys from the first row

    // Create header row with translated column names from existing translations and units
    const headers = columnKeys.map((key) => getColumnTranslation(t, key, units));

    // Set up columns with headers and width
    excelSheet.columns = columnKeys.map((key, index) => ({
      key,
      header: headers[index],
      width: key === "date" ? 15 : 20,
    }));

    // Style header row
    const headerRow = excelSheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center" };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "F0F0F0" },
    };

    // Add data rows
    sheetData.forEach((rowData) => {
      const row = excelSheet.addRow(rowData);
      // Right-align numeric columns
      row.eachCell((cell, colNumber) => {
        const key = columnKeys[colNumber - 1];
        if (key !== "date") {
          cell.alignment = { horizontal: "right" };
        }
      });
    });

    // Add borders to all cells
    excelSheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "D3D3D3" } },
          left: { style: "thin", color: { argb: "D3D3D3" } },
          bottom: { style: "thin", color: { argb: "D3D3D3" } },
          right: { style: "thin", color: { argb: "D3D3D3" } },
        };
      });
    });

  } catch (error) {
    console.error("Error creating processed data sheet:", error);
    workbook.removeWorksheet(sheetName); // Remove sheet if error occurs
  }
};

/**
 * Processes cleanData from column-based format to row-based format for Excel export
 * Extracts both data and units from the API response
 * @param {Object} cleanData - Column-based data object from API
 * @returns {Object|null} - Object with processedData array and units, or null if no valid data
 */
export const processCleanDataForExcel = (cleanData, data_units) => {
  if (!cleanData || typeof cleanData !== "object" || Object.keys(cleanData).length === 0) {
    return null;
  }

  try {
    // Extract units from the cleanData response
    // Units are typically stored in a separate property or can be extracted from column metadata
    const units = {
      temperature: '°' + (data_units?.temperature),
      pressure: data_units?.pressure,
      flow: data_units?.flow,
      feed_quality: data_units?.feed_quality,
      permeate_quality: data_units?.permeate_quality,
    };
    // Get all column keys from cleanData (excluding 'units' key)
    const columnKeys = Object.keys(cleanData).filter(key => key !== "units");
    
    if (columnKeys.length === 0) {
      return null;
    }

    // Get the indices from the first column (all columns should have same indices)
    const firstColumn = cleanData[columnKeys[0]];
    
    // Handle both array and object formats
    let indices = [];
    if (Array.isArray(firstColumn)) {
      indices = firstColumn.map((_, idx) => String(idx));
    } else if (firstColumn && typeof firstColumn === "object") {
      // Get indices and sort them numerically
      indices = Object.keys(firstColumn).sort((a, b) => Number(a) - Number(b));
    }
    
    if (indices.length === 0) {
      return null;
    }

    // Transform the data from column-based to row-based format
    let processedData = indices.map((index) => {
      const row = {};
      columnKeys.forEach((colKey) => {
        let value;
        
        // Handle both array and object formats
        if (Array.isArray(cleanData[colKey])) {
          value = cleanData[colKey][Number(index)];
        } else {
          value = cleanData[colKey]?.[index];
        }
        
        // Handle date formatting
        if (colKey === "date" && value) {
          try {
            const dateObj = new Date(value);
            if (!isNaN(dateObj.getTime())) {
              const formattedDay = dateObj.getDate().toString().padStart(2, "0");
              const formattedMonth = (dateObj.getMonth() + 1).toString().padStart(2, "0");
              const formattedYear = dateObj.getFullYear();
              value = `${formattedDay}/${formattedMonth}/${formattedYear}`;
            }
          } catch (e) {
            // Keep original value if date parsing fails
          }
        }
        
        // Handle null/undefined values
        if (value === null || value === undefined) {
          value = "";
        }
        
        // Round numeric values to reasonable precision
        if (typeof value === "number" && !isNaN(value)) {
          value = Math.round(value * 10000) / 10000;
        }
        
        row[colKey] = value;
      });
      return row;
    });
    
    // Filter out empty rows (all values are empty strings)
    processedData = processedData.filter((row) => {
      return Object.values(row).some((val) => val !== "");
    });
    
    // If no valid data after filtering, return null
    if (processedData.length === 0) {
      return null;
    }

    return {
      data: processedData,
      units: units,
    };
  } catch (error) {
    console.error("Error processing cleanData:", error);
    return null;
  }
};

export default createProcessedDataExcelSheet;
